/**
 * Dashboard autoplay - shared numbers.
 *
 * The bounds are shared by both interval inputs: the per-session one in the start modal and
 * the global default in Application settings, so a value accepted in one is accepted in the other.
 */

export const AUTOPLAY_INTERVAL_MIN_S = 5;
export const AUTOPLAY_INTERVAL_MAX_S = 3600;
/** Used whenever the configured default is missing, unparsable or out of range. */
export const AUTOPLAY_INTERVAL_FALLBACK_S = 30;

export const AUTOPLAY_PRESETS_S = [10, 30, 60] as const;

/**
 * How long a dashboard may take to mount before autoplay gives up on it and moves on.
 * A single slow or broken dashboard must not stall the whole loop.
 */
export const AUTOPLAY_MOUNT_TIMEOUT_MS = 15_000;

/** Countdown resolution - also how often the mount timeout is checked. */
export const AUTOPLAY_TICK_MS = 250;

/** A folder needs at least this many dashboards for playback to make sense. */
export const AUTOPLAY_MIN_DASHBOARDS = 2;

/** Clamps anything coming from settings or an URL into a usable interval. */
export function normalizeAutoplayInterval(value: unknown): number {
  const seconds = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(seconds) || seconds < AUTOPLAY_INTERVAL_MIN_S || seconds > AUTOPLAY_INTERVAL_MAX_S)
    return AUTOPLAY_INTERVAL_FALLBACK_S;

  return Math.round(seconds);
}

export function formatAutoplayInterval(seconds: number): string {
  if (seconds % 60 === 0 && seconds >= 60) {
    const minutes = seconds / 60;
    return `${minutes} min`;
  }
  return `${seconds} s`;
}
